<template>
  <button
    v-if="isMobileViewport && !isKanbanRoute"
    class="mobile-submenu-toggle"
    :class="{ open: isMobileMenuOpen }"
    type="button"
    :aria-expanded="isMobileMenuOpen ? 'true' : 'false'"
    :aria-label="t('submenu.mobile.openMenu')"
    @click="toggleMobileMenu"
  >
    <span></span>
    <span></span>
    <span></span>
  </button>

  <div
    v-if="isMobileViewport && isMobileMenuOpen"
    class="mobile-submenu-overlay"
    @click="closeMobileMenu"
  ></div>

  <div
    class="workspace-submenu premium-submenu"
    :class="{ 'mobile-open': isMobileMenuOpen, 'mobile-closed': isMobileViewport && !isMobileMenuOpen }"
    :style="workspaceColorStyle"
  >
    <!-- Header com Logo ou Workspace -->
    <div class="submenu-header">
      <div class="submenu-logo">
        <!-- Header Premium do Home -->
        <template v-if="isHome">
          <div class="home-header-premium">
            <!-- Background gradient sutil -->
            <!-- <div class="home-background"></div> -->
            
            <!-- Container principal -->
            <div class="home-content-premium">
              <!-- Logo centralizada -->
              <div class="home-logo-container">
                <img 
                  :src="logoUrl" 
                  alt="BackLog" 
                  class="home-logo-premium"
                />
              </div>
              
              <!-- Informações do sistema -->
              <div class="home-info-premium">
                <!-- Subtítulo elegante -->
                <p class="system-subtitle">{{ t('submenu.home.systemSubtitle') }}</p>
                
                <div class="profile-preview-premium">
                  <div class="profile-avatar-wrap">
                    <img
                      v-if="profileAvatar"
                      :src="profileAvatar"
                      :alt="profileName"
                      class="profile-avatar-image"
                    />
                    <div v-else class="profile-avatar-fallback">
                      {{ profileInitials }}
                    </div>
                  </div>

                  <div class="profile-main-info">
                    <span class="profile-name">{{ profileName }}</span>
                    <span class="profile-email">{{ profileEmail }}</span>
                  </div>
                </div>

                <div class="submenu-language-switcher">
                  <LanguageSwitcher variant="compact" :show-label="false" />
                </div>
              </div>
            </div>
          </div>
        </template>
        
        <!-- Workspace selecionado - Header Premium -->
        <template v-else-if="currentWorkspace">
          <div class="workspace-header-premium">
                        
            <!-- Container principal -->
            <div class="workspace-content-premium">
              <!-- Área do logo: mesmo tamanho com ou sem imagem -->
              <div class="workspace-icon-premium">
                <img
                  v-if="showWorkspacePhoto"
                  :src="currentWorkspacePhoto"
                  :alt="currentWorkspace.nome"
                  class="workspace-image-premium"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="workspace-image-premium workspace-image-placeholder"
                  :style="workspacePlaceholderStyle"
                  role="img"
                  :aria-label="currentWorkspace.nome"
                >
                  <component
                    :is="getIconComponent(currentWorkspace.icone)"
                    class="workspace-placeholder-icon"
                  />
                </div>

                <!-- Badge de status -->
                <div class="workspace-status-badge">
                  <div class="status-dot"></div>
                </div>
              </div>
              
              <!-- Informações do workspace -->
              <div class="workspace-info-premium">
                <h3 class="workspace-name-premium">{{ currentWorkspace.nome }}</h3>
                <div class="workspace-type-premium">
                  <span class="type-badge">{{ workspaceTypeLabel(currentWorkspace.tipo) }}</span>
                </div>
              </div>
            </div>
            
            <!-- Efeito de brilho -->
            <div class="workspace-shine-effect"></div>
          </div>
        </template>
      </div>
    </div>

    <!-- Separador Premium -->
    <div class="premium-separator"></div>

    <!-- Menu de Opções -->
    <div class="submenu-options">
      <!-- Menu para Home -->
      <template v-if="isHome">
        <div class="option-group">
          <div class="group-title">{{ t('submenu.home.navigation') }}</div>
          
          <button 
            @click="navigateTo('/dashboard')"
            class="submenu-option premium-option"
            :class="{ 'active': currentRoute === '/dashboard' || currentRoute === '/system-dashboard', 'option-locked': !hasScreenAccess('system-dashboard') }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.home.dashboard') }}</span>
            <svg v-if="!hasScreenAccess('system-dashboard')" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button
            v-show="UPGRADE_SUBMENU_VISIBLE"
            @click="navigateTo('/upgrade')"
            class="submenu-option premium-option"
            :class="{ 'active': currentRoute === '/upgrade', 'option-locked': !hasScreenAccess('upgrade') }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.home.upgrade') }}</span>
            <svg v-if="!hasScreenAccess('upgrade')" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            @click="navigateTo('/meu-perfil')"
            class="submenu-option premium-option"
            :class="{ 'active': currentRoute === '/meu-perfil', 'option-locked': !hasScreenAccess('meu-perfil') }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A8 8 0 1118.88 17.8M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.home.myProfile') }}</span>
            <svg v-if="!hasScreenAccess('meu-perfil')" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button
            v-show="SHOW_ESTRUTURACAO_UI"
            @click="navigateTo('/estruturacao')"
            class="submenu-option premium-option"
            :class="{ active: currentRoute === '/estruturacao' }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.home.estruturacao') }}</span>
          </button>

          <button
            @click="navigateTo('/suporte')"
            class="submenu-option premium-option"
            :class="{ active: currentRoute === '/suporte' }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.home.support') }}</span>
          </button>

          <button 
            v-if="canAccessSystemAdmin"
            @click="navigateTo('/super-admin')"
            class="submenu-option premium-option"
            :class="{ 'active': currentRoute.startsWith('/super-admin'), 'option-locked': !hasScreenAccess('super-admin') }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6l3 6 6 .5-4.5 4 1.5 6.5L12 20l-6 3 1.5-6.5L3 12.5 9 12l3-6z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.home.superAdmin') }}</span>
            <svg v-if="!hasScreenAccess('super-admin')" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>
        </div>
      </template>

      <!-- Menu para Workspace -->
      <template v-else-if="currentWorkspace">
        <div class="option-group">
          <div class="group-title">{{ workspaceTypeLabel(currentWorkspace.tipo) }}</div>
          
          <button 
            @click="navigateTo(`/workspace/${currentWorkspace.id}/dashboard`)"
            class="submenu-option premium-option"
            :class="{ 'active': currentRoute.includes(`/workspace/${currentWorkspace.id}/dashboard`) }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.dashboard') }}</span>
          </button>

          <button 
            v-if="isServicosWorkspace(currentWorkspace)"
            @click="navigateTo(workspacePath('clientes'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/clientes`),
              'option-locked': isWorkspacePlanLocked(workspacePath('clientes'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.clientes') }}</span>
            <svg v-if="isWorkspacePlanLocked(workspacePath('clientes'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            @click="navigateTo(workspacePath('configuracoes'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/configuracoes`),
              'option-locked': isWorkspaceStaffLocked(workspacePath('configuracoes'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.settings') }}</span>
            <svg v-if="isWorkspaceStaffLocked(workspacePath('configuracoes'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            v-if="isCrmWorkspace(currentWorkspace) || isServicosWorkspace(currentWorkspace)"
            @click="navigateTo(workspacePath('integracoes'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/integracoes`),
              'option-locked': isWorkspaceStaffLocked(workspacePath('integracoes'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.integrations') }}</span>
            <svg v-if="isWorkspaceStaffLocked(workspacePath('integracoes'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            v-if="isCrmWorkspace(currentWorkspace)"
            @click="navigateTo(workspacePath('orcamentos'))"
            class="submenu-option premium-option"
            :class="{ active: currentRoute.includes(`/workspace/${currentWorkspace.id}/orcamentos`) }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.orcamentos') }}</span>
          </button>

          <button 
            v-if="currentWorkspace?.tipo === 'CRM'"
            @click="navigateTo(workspacePath('relatorios'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/relatorios`),
              'option-locked': isWorkspacePlanLocked(workspacePath('relatorios'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.reports') }}</span>
            <svg v-if="isWorkspacePlanLocked(workspacePath('relatorios'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            v-if="isTarefasWorkspace(currentWorkspace)"
            @click="navigateTo(workspacePath('historia'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/historia`),
              'option-locked': isWorkspacePlanLocked(workspacePath('historia'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.history') }}</span>
            <svg v-if="isWorkspacePlanLocked(workspacePath('historia'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            @click="navigateTo(workspacePath('usuarios'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/usuarios`),
              'option-locked': isWorkspaceStaffLocked(workspacePath('usuarios'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.users') }}</span>
            <svg v-if="isWorkspaceStaffLocked(workspacePath('usuarios'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            @click="navigateTo(workspacePath('agenda'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/agenda`),
              'option-locked': isWorkspacePlanLocked(workspacePath('agenda'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.agenda') }}</span>
            <svg v-if="isWorkspacePlanLocked(workspacePath('agenda'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button
            v-if="isTarefasWorkspace(currentWorkspace) || isServicosWorkspace(currentWorkspace)"
            @click="navigateTo(workspacePath('financeiro'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/financeiro`),
              'option-locked': isWorkspaceStaffLocked(workspacePath('financeiro')) || isWorkspacePlanLocked(workspacePath('financeiro'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.financeiro') }}</span>
            <svg v-if="isWorkspaceStaffLocked(workspacePath('financeiro')) || isWorkspacePlanLocked(workspacePath('financeiro'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button
            v-if="isTarefasWorkspace(currentWorkspace)"
            @click="navigateTo(workspacePath('atividade'))"
            class="submenu-option premium-option"
            :class="{
              active: currentRoute.includes(`/workspace/${currentWorkspace.id}/atividade`),
              'option-locked': isWorkspaceStaffLocked(workspacePath('atividade')) || isWorkspacePlanLocked(workspacePath('atividade'))
            }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.atividade') }}</span>
            <svg v-if="isWorkspaceStaffLocked(workspacePath('atividade')) || isWorkspacePlanLocked(workspacePath('atividade'))" class="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </button>

          <button 
            v-if="!isServicosWorkspace(currentWorkspace)"
            @click="navigateTo(workspacePath('kanban'))"
            class="submenu-option premium-option"
            :class="{ 'active': currentRoute.includes(`/workspace/${currentWorkspace.id}/kanban`) }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.kanban') }}</span>
          </button>

          <!-- APPs: oculto até implementação futura (habilitar com APPS_MENU_ENABLED) -->
          <button 
            v-if="isSuperAdmin && APPS_MENU_ENABLED"
            @click="navigateTo(`/workspace/${currentWorkspace.id}/apps`)"
            class="submenu-option premium-option"
            :class="{ 'active': currentRoute.includes(`/workspace/${currentWorkspace.id}/apps`) }"
          >
            <div class="option-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 3a.75.75 0 000 1.5h1.5V6h-1.5a.75.75 0 000 1.5h1.5V9h-1.5a.75.75 0 000 1.5h1.5V12h-1.5a.75.75 0 000 1.5h1.5V15h-1.5a.75.75 0 000 1.5h1.5V18h-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0V19.5h1.5a.75.75 0 000-1.5H13.5V16.5H15a.75.75 0 000-1.5h-1.5V13.5H15a.75.75 0 000-1.5h-1.5V10.5H15a.75.75 0 000-1.5h-1.5V7.5H15a.75.75 0 000-1.5h-1.5V4.5H15a.75.75 0 000-1.5h-5.25z"/>
              </svg>
            </div>
            <span class="option-text">{{ t('submenu.workspace.apps') }}</span>
          </button>
        </div>
      </template>
    </div>
  </div>

  <PlanUpgradeModal
    v-model="showPlanUpgradeModal"
    :title="planUpgradeTitle"
    :description="planUpgradeDescription"
  />

  <WorkspaceAccessDeniedModal
    v-model="showAccessDeniedModal"
    :area="accessDeniedArea"
  />
</template>

<script setup>
import { computed, h, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useThemeStore } from '../stores/theme'
import { useAuthStore } from '../stores/auth'
import apiClient from '../services/api'
import PlanUpgradeModal from './plan/PlanUpgradeModal.vue'
import WorkspaceAccessDeniedModal from './workspace/WorkspaceAccessDeniedModal.vue'
import LanguageSwitcher from './LanguageSwitcher.vue'
import { usePlanoFeatures } from '../composables/usePlanoFeatures'
import { useWorkspaceAccess, setWorkspaceCreator } from '../composables/useWorkspaceAccess'
import { getPlanFeatureForWorkspaceRoute, isStaffOnlyRoute, parseWorkspaceRouteSegment } from '../utils/planRouteGuards'
import { useAccountAccessBlock } from '../composables/useAccountAccessBlock'
import { setOwnerAccessBlocked } from '../composables/useAccountAccessBlock'
import { isServicosWorkspace, isCrmWorkspace, isTarefasWorkspace } from '../utils/workspaceTipo'
import { resolveMediaUrl } from '@/config/app'
import { SHOW_ESTRUTURACAO_UI } from '../constants/estruturacaoSections'

const props = defineProps({
  currentWorkspace: {
    type: Object,
    default: null
  },
  isHome: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const route = useRoute()
const { t, te } = useI18n()
const themeStore = useThemeStore()
const authStore = useAuthStore()

const currentRoute = computed(() => route.path)
const failedWorkspaceImages = ref(new Set())
const planoCodigoAtual = ref('')
const isMobileViewport = ref(false)
const isMobileMenuOpen = ref(false)
const isKanbanRoute = computed(() => route.path.includes('/kanban'))

const currentWorkspacePhoto = computed(() =>
  resolveMediaUrl(props.currentWorkspace?.fotoWorkspace)
)

const showWorkspacePhoto = computed(() => {
  const ws = props.currentWorkspace
  if (!ws) return false
  const foto = String(ws.fotoWorkspace || '').trim()
  if (!foto) return false
  return !failedWorkspaceImages.value.has(ws.id)
})

const workspacePlaceholderStyle = computed(() => {
  const cor = props.currentWorkspace?.corWorkspace || '#5865F2'
  const corLight = adjustColorLightness(cor, 18)
  return {
    background: `linear-gradient(145deg, ${cor} 0%, ${corLight} 100%)`
  }
})

const screenPermissions = ref({})
const permissionsLoaded = ref(false)
const isSuperAdmin = computed(() => {
  const roleName = authStore.user?.role?.nome
  return roleName === 'SUPER_ADMIN'
})

const canAccessSystemAdmin = computed(() => {
  const roleName = authStore.user?.role?.nome
  return roleName === 'SUPER_ADMIN' || roleName === 'ADMIN'
})

/** Habilitar quando a área APPs estiver pronta */
const APPS_MENU_ENABLED = false

/** Exibir opção Upgrade no submenu Home (rota /upgrade permanece disponível) */
const UPGRADE_SUBMENU_VISIBLE = false
const profileName = computed(() => {
  return authStore.user?.nomeCompleto || authStore.user?.nome || authStore.user?.email?.split('@')?.[0] || t('submenu.defaultUser')
})
const profileEmail = computed(() => authStore.user?.email || t('submenu.noEmail'))
const profileAvatar = computed(() => authStore.user?.fotoPerfil || '')
const profileInitials = computed(() => {
  const parts = profileName.value.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'U'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
})
const planoFeatures = usePlanoFeatures()
const workspaceAccess = useWorkspaceAccess()
const accountBlock = useAccountAccessBlock()
const showPlanUpgradeModal = ref(false)
const planUpgradeTitle = ref('')
const planUpgradeDescription = ref('')
const showAccessDeniedModal = ref(false)
const accessDeniedArea = ref('')

const STAFF_ROUTE_AREAS = {
  configuracoes: () => t('configuracoes.area'),
  usuarios: () => t('usuarios.area'),
  integracoes: () => t('integracao.area'),
  financeiro: () => t('workspaceFinanceiro.area'),
  atividade: () => t('workspaceAtividade.area'),
  clientes: () => t('workspaceClientes.area')
}

const workspacePath = (segment) => {
  const id = props.currentWorkspace?.id
  return id ? `/workspace/${id}/${segment}` : '/'
}

const isWorkspaceStaffLocked = (path) => {
  const wsId = props.currentWorkspace?.id
  if (!wsId || !workspaceAccess.isRoleLoadedFor(wsId)) return false
  if (!isStaffOnlyRoute(path)) return false
  return !workspaceAccess.canManageWorkspaceSettings.value
}

const isWorkspacePlanLocked = (path) => {
  const ws = props.currentWorkspace
  const feature = getPlanFeatureForWorkspaceRoute(path, ws?.tipo)
  if (!feature) return false
  const wsId = ws?.id
  if (!wsId) return false
  return !planoFeatures.can(feature)
}

const staffAreaLabelForPath = (path) => {
  const segment = parseWorkspaceRouteSegment(path)
  const fn = STAFF_ROUTE_AREAS[segment]
  return fn ? fn() : t('workspaceDenied.defaultArea')
}

const openWorkspaceAccessDenied = (path) => {
  accessDeniedArea.value = staffAreaLabelForPath(path)
  showAccessDeniedModal.value = true
}

const workspaceTypeLabel = (tipo) => {
  if (tipo === 'CRM') return t('submenu.workspaceType.crm')
  if (tipo === 'SERVICOS') return t('submenu.workspaceType.servicos')
  if (tipo === 'TAREFAS' || tipo === 'TASK') return t('submenu.workspaceType.task')
  return tipo || t('submenu.workspaceType.task')
}

const loadWorkspacePlanContext = async () => {
  const wsId = props.currentWorkspace?.id
  if (!wsId || props.isHome) return
  await Promise.all([
    planoFeatures.loadForWorkspace(wsId),
    workspaceAccess.loadRoleForWorkspace(wsId)
  ])
}

watch(
  () => props.currentWorkspace,
  (ws) => {
    if (ws?.id) {
      const criadorId = ws.criadorId ?? ws.criador?.id ?? null
      setWorkspaceCreator(ws.id, criadorId)
    }
    loadWorkspacePlanContext()
  },
  { immediate: true, deep: true }
)

const loadScreenPermissions = async () => {
  try {
    const response = await apiClient.get('/permissoes-tela/me')
    screenPermissions.value = response.data
    permissionsLoaded.value = true
  } catch (error) {
    console.error('[SUBMENU] Erro ao carregar permissões de tela:', error)
    permissionsLoaded.value = true
  }
}

const loadMeuPlano = async () => {
  try {
    const response = await apiClient.get('/planos/meu-plano')
    const codigo = String(response.data?.codigo || '').toUpperCase()
    planoCodigoAtual.value = codigo
    if (codigo) {
      authStore.updateUser({ planoCodigo: codigo })
    }
  } catch (error) {
    console.warn('[SUBMENU] Erro ao carregar plano atual:', error)
  }
}

const hasScreenAccess = (screenId) => {
  if (screenId === 'super-admin') {
    return canAccessSystemAdmin.value
  }
  if (!permissionsLoaded.value) return true
  if (screenPermissions.value[screenId] === undefined) return true
  return screenPermissions.value[screenId] === true
}

onMounted(() => {
  updateMobileViewport()
  window.addEventListener('resize', updateMobileViewport)
  window.addEventListener('toggle-workspace-submenu', handleExternalToggle)
  if (props.isHome) {
    loadScreenPermissions()
    loadMeuPlano()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateMobileViewport)
  window.removeEventListener('toggle-workspace-submenu', handleExternalToggle)
})

watch(() => props.isHome, (newVal) => {
  if (newVal && !permissionsLoaded.value) {
    loadScreenPermissions()
  }
  if (newVal && !planoCodigoAtual.value) {
    loadMeuPlano()
  }
})

watch(
  () => route.path,
  () => {
    closeMobileMenu()
  }
)

const logoUrl = computed(() => {
  // Usar logo baseado no tema atual
  if (themeStore.isLight) {
    // Tema claro - usar logo_l_p.png
    return new URL('../assets/logo_l_p.png', import.meta.url).href 
  } else {
    // Tema escuro - usar logo_l_w.png
    return new URL('../assets/logo_l_w.png', import.meta.url).href
  }
})

const workspaceBackgroundStyle = computed(() => {
  if (!props.currentWorkspace) return {}
  
  const baseColor = props.currentWorkspace.corWorkspace || '#5865F2'
  const lightColor = adjustColorLightness(baseColor, 30)
  const darkColor = adjustColorLightness(baseColor, -20)
  
  return {
    background: `linear-gradient(135deg, ${lightColor} 0%, ${baseColor} 50%, ${darkColor} 100%)`,
    boxShadow: `0 8px 32px rgba(${hexToRgb(baseColor)}, 0.3)`
  }
})

const workspaceColorStyle = computed(() => {
  if (!props.currentWorkspace || props.isHome) return {}
  
  const baseColor = props.currentWorkspace.corWorkspace || '#5865F2'
  const lightColor = adjustColorLightness(baseColor, 20)
  
  return {
    '--workspace-color': baseColor,
    '--workspace-color-light': lightColor,
    '--workspace-color-rgb': hexToRgb(baseColor)
  }
})

const cacheLastWorkspaceRoute = (path) => {
  if (!props.currentWorkspace || props.isHome) return

  const workspaceId = props.currentWorkspace.id
  if (!workspaceId) return

  const workspacePrefix = `/workspace/${workspaceId}/`
  if (!path.startsWith(workspacePrefix)) return

  if (
    isStaffOnlyRoute(path) &&
    workspaceAccess.isRoleLoadedFor(workspaceId) &&
    !workspaceAccess.canManageWorkspaceSettings.value
  ) {
    return
  }

  localStorage.setItem(`workspace-last-route-${workspaceId}`, path.split('?')[0])
}

const routeToScreenMap = {
  '/dashboard': 'system-dashboard',
  '/system-dashboard': 'system-dashboard',
  '/upgrade': 'upgrade',
  '/meu-perfil': 'meu-perfil',
  '/super-admin': 'super-admin'
}

const updateMobileViewport = () => {
  if (typeof window === 'undefined') return
  isMobileViewport.value = window.matchMedia('(max-width: 768px)').matches
  if (!isMobileViewport.value) {
    isMobileMenuOpen.value = false
  }
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const handleExternalToggle = () => {
  if (!isMobileViewport.value) return
  toggleMobileMenu()
}

const openPlanUpgrade = (featureKey) => {
  const featurePath = `submenu.planUpgrade.features.${featureKey}`
  const featureLabel = te(featurePath) ? t(featurePath) : t('submenu.planUpgrade.defaultFeature')
  planUpgradeTitle.value = t('submenu.planUpgrade.title', { feature: featureLabel })
  planUpgradeDescription.value = t('submenu.planUpgrade.description')
  showPlanUpgradeModal.value = true
}

const openInPageBlockForPath = (path) => {
  if (props.currentWorkspace?.criador?.acessoBloqueado) {
    setOwnerAccessBlocked(true)
  } else if (planoFeatures.ownerAccessBlocked?.value ?? planoFeatures.ownerAccessBlocked) {
    setOwnerAccessBlocked(true)
  } else {
    setOwnerAccessBlocked(false)
  }
  accountBlock.openInPageBlock()
}

const navigateTo = async (path) => {
  const isDevWorkspaceRoute = /\/workspace\/\d+\/apps$/.test(path)
  if (isDevWorkspaceRoute && !isSuperAdmin.value) {
    router.push('/acesso-negado')
    closeMobileMenu()
    return
  }

  if (props.currentWorkspace?.id && !props.isHome) {
    await loadWorkspacePlanContext()
    const wsId = props.currentWorkspace.id
    if (accountBlock.shouldBlockNavigation(path)) {
      openInPageBlockForPath(path)
      closeMobileMenu()
      return
    }
    if (
      isStaffOnlyRoute(path) &&
      workspaceAccess.isRoleLoadedFor(wsId) &&
      !workspaceAccess.canManageWorkspaceSettings.value
    ) {
      openWorkspaceAccessDenied(path)
      closeMobileMenu()
      return
    }
    const planFeature = getPlanFeatureForWorkspaceRoute(path, props.currentWorkspace?.tipo)
    if (planFeature && !planoFeatures.can(planFeature)) {
      openPlanUpgrade(planFeature)
      closeMobileMenu()
      return
    }
  }

  const screenId = routeToScreenMap[path]
  if (screenId && !hasScreenAccess(screenId)) {
    router.push({ path: '/acesso-negado', query: { tela: screenId } })
    closeMobileMenu()
    return
  }
  cacheLastWorkspaceRoute(path)
  router.push(path)
  closeMobileMenu()
}

// Função para lidar com erro de carregamento de imagem
const handleImageError = (event) => {
  console.log('Erro ao carregar imagem do workspace:', event)
  if (props.currentWorkspace) {
    failedWorkspaceImages.value = new Set([
      ...failedWorkspaceImages.value,
      props.currentWorkspace.id
    ])
  }
}

// Ícones SVG como componentes (mesmos do WorkspaceSidebar)
const TaskIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4'
      })
    ])
  }
}

const UsersIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
      })
    ])
  }
}

const FolderIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
      })
    ])
  }
}

const BriefcaseIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6'
      })
    ])
  }
}

const ChartIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
      })
    ])
  }
}

const LightbulbIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
      })
    ])
  }
}

const HeartIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
      })
    ])
  }
}

const StarIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'
      })
    ])
  }
}

const RocketIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M13 10V3L4 14h7v7l9-11h-7z'
      })
    ])
  }
}

const ShieldIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
      })
    ])
  }
}

const CogIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
      }),
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z'
      })
    ])
  }
}

const LetterIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-6 h-6'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M4 6h16M4 12h16M4 18h16'
      })
    ])
  }
}

// Função para obter o componente de ícone correto
const getIconComponent = (iconName) => {
  const iconMap = {
    'task': TaskIcon,
    'users': UsersIcon,
    'folder': FolderIcon,
    'briefcase': BriefcaseIcon,
    'chart': ChartIcon,
    'lightbulb': LightbulbIcon,
    'heart': HeartIcon,
    'star': StarIcon,
    'rocket': RocketIcon,
    'shield': ShieldIcon,
    'cog': CogIcon,
    'letter': LetterIcon
  }
  
  return iconMap[iconName] || LetterIcon
}

// Funções auxiliares para manipulação de cores
const adjustColorLightness = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = (num >> 16) + amt
  const G = (num >> 8 & 0x00FF) + amt
  const B = (num & 0x0000FF) + amt
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)
}

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '86, 101, 242'
}
</script>

<style scoped>
.mobile-submenu-toggle,
.mobile-submenu-overlay {
  display: none;
}

.workspace-submenu {
  width: 240px;
  height: 100vh;
  background-color: var(--bg-secondary);
  border-right: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 80px; /* Posicionar ao lado da sidebar principal */
  top: 0;
  z-index: 40;
  transition: all 0.3s ease;
}

/* Header com Logo */
.submenu-header {
  padding: 0;
  border-bottom: 1px solid var(--border-primary);
  overflow: hidden;
}

.submenu-logo {
  display: block;
  width: 100%;
}

.submenu-logo-full {
  width: 100%;
  height: auto;
  max-height: 48px;
  object-fit: contain;
  border-radius: 8px;
}

/* Estilos para o workspace no header */
.workspace-icon-container {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
}

.submenu-workspace-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.submenu-workspace-icon {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
}

.workspace-icon-svg {
  width: 18px;
  height: 18px;
}

.submenu-logo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.logo-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.logo-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  line-height: 1;
}

/* Separador Premium */
.premium-separator {
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border-primary), transparent);
  margin: 16px 0;
}

/* Opções do Menu */
.submenu-options {
  flex: 1;
  padding: 0 8px;
  overflow-y: auto;
}

.option-group {
  margin-bottom: 24px;
}

.group-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px 16px;
  padding-top: 16px;
}

.submenu-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin: 2px 0;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.submenu-option:hover {
  background-color: rgba(var(--workspace-color-rgb, 0, 0, 0), 0.1);
  color: var(--text-primary);
  transform: translateX(2px);
  border-left: 2px solid white;
}

/* Tema claro - borda preta no hover */
body.tema_clara .submenu-option:hover {
  border-left: 2px solid black;
}

.submenu-option.active {
  background-color: white;
  color: black;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.submenu-option.active .option-icon {
  color: black;
}

/* Tema claro - background preto */
body.tema_clara .submenu-option.active {
  background-color: black;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

body.tema_clara .submenu-option.active .option-icon {
  color: white;
}

.option-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  transition: color 0.2s ease;
}

.option-text {
  flex: 1;
  font-weight: 500;
}

/* Scrollbar personalizada */
.submenu-options::-webkit-scrollbar {
  width: 6px;
}

.submenu-options::-webkit-scrollbar-track {
  background: transparent;
}

.submenu-options::-webkit-scrollbar-thumb {
  background-color: var(--border-primary);
  border-radius: 3px;
}

.submenu-options::-webkit-scrollbar-thumb:hover {
  background-color: var(--text-tertiary);
}

/* Menu item bloqueado */
.submenu-option.option-locked {
  opacity: 0.45;
}

.submenu-option.option-locked:hover {
  opacity: 0.6;
}

.lock-icon {
  width: 14px;
  height: 14px;
  color: var(--text-tertiary);
  flex-shrink: 0;
  opacity: 0.7;
}

/* Responsividade */
@media (max-width: 768px) {
  .mobile-submenu-toggle {
    position: fixed;
    top: 12px;
    left: 10px;
    z-index: 95;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--border-primary);
    background: var(--bg-secondary);
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  }

  .mobile-submenu-toggle span {
    width: 16px;
    height: 2px;
    border-radius: 999px;
    background: var(--text-primary);
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .mobile-submenu-toggle.open span:nth-child(1) {
    transform: translateY(6px) rotate(45deg);
  }

  .mobile-submenu-toggle.open span:nth-child(2) {
    opacity: 0;
  }

  .mobile-submenu-toggle.open span:nth-child(3) {
    transform: translateY(-6px) rotate(-45deg);
  }

  .mobile-submenu-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.42);
    z-index: 85;
    display: block;
  }

  .workspace-submenu {
    width: min(280px, calc(100vw - 56px));
    left: 0;
    transform: translateX(-115%);
    opacity: 0;
    pointer-events: none;
    z-index: 90;
    box-shadow: 10px 0 24px rgba(0, 0, 0, 0.24);
  }

  .workspace-submenu.mobile-open {
    transform: translateX(0);
    opacity: 1;
    pointer-events: auto;
  }

  .workspace-submenu.mobile-closed {
    transform: translateX(-115%);
    opacity: 0;
    pointer-events: none;
  }
}

/* Animações */
.submenu-option {
  animation: fadeInUp 0.3s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Efeito de brilho para opções ativas */
.submenu-option.active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  border-radius: 8px;
  pointer-events: none;
}

/* ===== HEADER PREMIUM DO WORKSPACE ===== */
.workspace-header-premium {
  position: relative;
  min-height: 120px;
  background: var(--bg-secondary);
  border-radius: 0 0 16px 16px;
  overflow: hidden;
  margin-bottom: 8px;
}


.workspace-content-premium {
  position: relative;
  z-index: 2;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.workspace-icon-premium {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  margin: 0 auto 8px;
}

.workspace-image-premium {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: contain;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  display: block;
}

.workspace-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.workspace-placeholder-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.95);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
}

.workspace-image-placeholder .workspace-placeholder-icon :deep(svg) {
  width: 64px;
  height: 64px;
}

.workspace-icon-fallback {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.workspace-icon-svg-premium {
  width: 24px;
  height: 24px;
}

.workspace-status-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background: var(--success);
  border: 3px solid var(--bg-secondary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
}

.workspace-info-premium {
  text-align: center;
}

.workspace-name-premium {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace-type-premium {
  margin-bottom: 8px;
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
}

.workspace-stats {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--text-tertiary);
}

.stat-icon {
  font-size: 12px;
}

.stat-text {
  font-weight: 500;
}

.workspace-shine-effect {
  display: none;
}

/* Ajustes para tema claro */
body.tema_clara .workspace-header-premium {
  background: var(--bg-secondary);
}

body.tema_clara .workspace-image-premium,
body.tema_clara .workspace-icon-fallback,
body.tema_clara .workspace-image-placeholder {
  border-color: rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

body.tema_clara .workspace-shine-effect {
  background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.05), transparent);
}

/* ===== HEADER PREMIUM DO HOME ===== */
.home-header-premium {
  position: relative;
  min-height: 140px;
  background: var(--bg-secondary);
  /* border-radius: 0 0 20px 20px;
  overflow: hidden;
  margin-bottom: 8px;
  border: 1px solid var(--border-primary);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); */
}

.home-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(135deg, 
    rgba(59, 130, 246, 0.08) 0%, 
    rgba(147, 51, 234, 0.04) 25%,
    rgba(236, 72, 153, 0.06) 50%,
    rgba(34, 197, 94, 0.04) 75%,
    rgba(251, 191, 36, 0.06) 100%
  );
  opacity: 0.7;
  transition: all 0.4s ease;
}

.home-content-premium {
  position: relative;
  z-index: 2;
  padding: 24px 20px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.home-logo-container {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background-color: white;
  padding: 8px;
  border-radius: 8px;
}

.home-logo-premium {
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  position: relative;
  z-index: 2;
  display: block;
  filter: brightness(0);
}

/* Tema claro - background preto com logo branca */
body.tema_clara .home-logo-container {
  background-color: black;
}

body.tema_clara .home-logo-premium {
  filter: brightness(0) invert(1);
}

.home-info-premium {
  text-align: center;
  width: 100%;
}

.system-subtitle {
  position: relative;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  padding-top: 8px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  opacity: 0.9;
}
.system-subtitle::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 44px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--info), var(--success));
  box-shadow: 0 0 10px color-mix(in srgb, var(--accent-primary) 45%, transparent);
}

/* Preview de perfil premium */
.submenu-language-switcher {
  width: calc(100% + 20px);
  margin-left: -10px;
  margin-top: 4px;
}

.submenu-language-switcher :deep(.language-switcher) {
  width: 100%;
}

.submenu-language-switcher :deep(.language-switcher-wrap) {
  width: 100%;
}

.submenu-language-switcher :deep(.language-switcher-trigger) {
  width: 100%;
  min-width: 0;
  justify-content: flex-start;
}

.submenu-language-switcher :deep(.language-switcher-menu) {
  z-index: 60;
}

.submenu-header:has(.submenu-language-switcher) {
  overflow: visible;
}

.profile-preview-premium {
  display: flex;
  align-items: center;
  width: calc(100% + 20px);
  margin-left: -10px;
  gap: 12px;
  margin-bottom: 0;
  padding: 10px 12px;
  border-radius: 0;
  border: 1px solid color-mix(in srgb, var(--accent-primary) 34%, rgba(255, 255, 255, 0.12));
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--accent-primary) 22%, transparent), transparent 65%),
    linear-gradient(140deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.09),
    0 8px 20px rgba(2, 6, 23, 0.24);
  backdrop-filter: blur(8px);
}

.profile-avatar-wrap {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: visible;
  border: 1px solid color-mix(in srgb, var(--accent-primary) 48%, rgba(255, 255, 255, 0.22));
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
  isolation: isolate;
}
.avatar-verified-dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #22c55e;
  color: #ffffff;
  border: 2px solid var(--bg-secondary);
  box-shadow: 0 2px 6px rgba(34, 197, 94, 0.35);
  z-index: 3;
}
.avatar-verified-dot-negative {
  background: #ef4444;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35);
}
.avatar-verified-dot svg {
  width: 7px;
  height: 7px;
}

.profile-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 50%;
  position: relative;
  z-index: 1;
}

.profile-avatar-fallback {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #4f46e5 0%, #0ea5e9 55%, #06b6d4 100%);
}

.profile-main-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  text-align: left;
}

.profile-name {
  font-size: 12px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-email {
  font-size: 10px;
  font-weight: 500;
  color: var(--text-tertiary);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.verified-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  gap: 4px;
  padding: 4px 7px;
  border-radius: 7px;
  border: 1px solid rgba(34, 197, 94, 0.45);
  background: rgba(34, 197, 94, 0.12);
  color: #86efac;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.25px;
  text-transform: uppercase;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(34, 197, 94, 0.2);
  margin-top: 4px;
}
.verified-badge svg {
  width: 11px;
  height: 11px;
}
.verified-badge-negative {
  border-color: rgba(239, 68, 68, 0.45);
  background: rgba(239, 68, 68, 0.12);
  color: #fca5a5;
  box-shadow: 0 4px 10px rgba(239, 68, 68, 0.2);
}

.home-shine-effect {
  display: none;
}

/* Ajustes para tema claro - Home */
body.tema_clara .home-header-premium {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}

body.tema_clara .home-background {
  background: linear-gradient(135deg, 
    rgba(59, 130, 246, 0.06) 0%, 
    rgba(147, 51, 234, 0.03) 25%,
    rgba(236, 72, 153, 0.04) 50%,
    rgba(34, 197, 94, 0.03) 75%,
    rgba(251, 191, 36, 0.04) 100%
  );
  opacity: 0.8;
}



body.tema_clara .profile-preview-premium {
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.16), transparent 65%),
    linear-gradient(140deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.015));
  border-color: rgba(37, 99, 235, 0.2);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    0 8px 20px rgba(15, 23, 42, 0.08);
}

body.tema_clara .profile-avatar-wrap {
  border-color: rgba(37, 99, 235, 0.24);
}

body.tema_clara .verified-badge {
  color: #166534;
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(34, 197, 94, 0.34);
}
body.tema_clara .verified-badge-negative {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.14);
  border-color: rgba(239, 68, 68, 0.34);
}

body.tema_clara .avatar-verified-dot {
  border-color: var(--bg-secondary);
}


/* Responsividade */
@media (max-width: 768px) {
  .home-header-premium {
    min-height: 0;
  }
  
  .home-logo-container {
    width: auto;
    height: auto;
  }
  
  .home-logo-premium {
    height: auto;
    max-height: 36px;
  }
  
  .system-title {
    font-size: 16px;
  }

  .profile-preview-premium {
    width: 100%;
    margin-left: 0;
    padding: 6px 0;
    gap: 8px;
  }

  .profile-avatar-wrap {
    width: 32px;
    height: 32px;
  }

  .verified-badge {
    padding: 4px 6px;
    font-size: 8px;
  }

  /* Header do workspace: imagem à esquerda, textos à direita (após estilos base) */
  .workspace-submenu .workspace-header-premium {
    min-height: 0;
    margin: 0;
    background: transparent;
    border-radius: 0;
  }

  .workspace-submenu .workspace-content-premium {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    margin: 8px 10px 6px;
    border-radius: 14px;
    background: linear-gradient(145deg, var(--bg-tertiary) 0%, var(--bg-secondary) 55%, var(--bg-primary) 100%);
    border: 1px solid var(--border-primary);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.06) inset,
      0 6px 18px rgba(15, 23, 42, 0.1);
  }

  .workspace-submenu .workspace-icon-premium {
    width: 52px;
    min-width: 52px;
    height: 52px;
    aspect-ratio: 1;
    margin: 0;
    flex-shrink: 0;
  }

  .workspace-submenu .workspace-image-premium,
  .workspace-submenu .workspace-image-placeholder,
  .workspace-submenu .workspace-icon-fallback {
    width: 100%;
    height: 100%;
    border-width: 2px;
    border-radius: 12px;
    object-fit: cover;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  }

  .workspace-submenu .workspace-image-placeholder .workspace-placeholder-icon :deep(svg) {
    width: 26px;
    height: 26px;
  }

  .workspace-submenu .workspace-status-badge {
    width: 12px;
    height: 12px;
    bottom: 0;
    right: 0;
    border-width: 2px;
    box-shadow: 0 0 0 2px var(--bg-secondary);
  }

  .workspace-submenu .status-dot {
    width: 5px;
    height: 5px;
  }

  .workspace-submenu .workspace-info-premium {
    text-align: left;
    min-width: 0;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
  }

  .workspace-submenu .workspace-name-premium {
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .workspace-submenu .workspace-type-premium {
    margin: 0;
  }

  .workspace-submenu .type-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    border-radius: 999px;
    background: color-mix(in srgb, var(--workspace-color, #6366f1) 18%, var(--bg-tertiary));
    color: color-mix(in srgb, var(--workspace-color, #6366f1) 72%, var(--text-primary));
    border: 1px solid color-mix(in srgb, var(--workspace-color, #6366f1) 28%, transparent);
  }
}

body.tema_clara .workspace-submenu .workspace-content-premium {
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85) inset,
    0 4px 14px rgba(15, 23, 42, 0.08);
}
</style>
